import Container from 'components/ui/Container';
import PrimaryButton from 'components/ui/PrimaryButton';
import Spinner from 'components/ui/Spinner/spinner';
import useToggle from 'hooks/useToggle';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CoursesResponse, useListCoursesByTrainingQuery } from 'redux/reducers/courses';
import {
  IRoutineInfo,
  IRoutineSessionDetail,
  useCompleteModuleForRoutineMutation,
  useListRoutineByIdQuery,
  useListRoutineSessionQuery,
  useSaveRoutineQuery
} from 'redux/reducers/routine';
import editIcon from './Images/edit.png';

import { formatTime, getLocaleDateOnly } from 'utils/date';
import { tConvert } from 'utils/timeConvert';

import InviteUsersModal from './InviteUsersModal';
import DailySessionModal from './Modal/DailySessionModal';
import SessionModal from './Modal/SessionModal';
import RoutineSessionDailyDetail from './RoutineSessionDailyDetail';
import EndandStartImage from './EndandStartImage';

type Props = {};

type CoursesProps = {
  routineInfo: IRoutineInfo;
};

function CoursesElement({ routineInfo }: CoursesProps) {
  const [completeModuleForRoutine, { isLoading }] = useCompleteModuleForRoutineMutation();
  const { routineId = '' } = useParams<{ routineId: string }>();

  const handleOnCompleteClick = async (id: string) => {
    try {
      await completeModuleForRoutine({
        routineId: routineId,
        moduleId: id,
        body: { is_module_complete: true }
      }).unwrap();

      toast.success('Module is marked as complete!');
    } catch (error: any) {
      toast.error(error?.data?.message ?? 'Something went wrong!');
    }
  };
  let uniqueCourseNames: string[] = [];

  return (
    <div>
      {routineInfo?.tr_routine_coordinator?.map((item, index) => {
        if (
          item?.course_details?.course_name &&
          !uniqueCourseNames?.includes(item?.course_details?.course_name)
        ) {
          // Add the current course name to the array of unique course names
          uniqueCourseNames.push(item?.course_details?.course_name);

          return (
            <div className="row mb-3 align-items-center">
              <div className="col-4 col-md-3  align-items-start">
                <p>
                  <b>{item?.course_details?.course_name}</b>
                </p>
              </div>
              <div className="col-4 col-md-3 align-items-start">
                {routineInfo.tr_routine_coordinator.map(
                  (coordinator, index) =>
                    coordinator?.is_module_coordinator === true &&
                    coordinator?.course_details?.course_id === item?.course_details?.course_id && (
                      <React.Fragment key={index}>
                        {coordinator?.coordinator_details?.first_name +
                          ' ' +
                          (coordinator?.coordinator_details?.middle_name === null
                            ? ''
                            : coordinator?.coordinator_details?.middle_name) +
                          ' ' +
                          coordinator?.coordinator_details?.last_name}
                        {index !== routineInfo.tr_routine_coordinator.length - 1 && ', '}
                      </React.Fragment>
                    )
                )}
              </div>
              <div className="col-4 col-md-3 align-items-start" style={{ gap: '10px' }}>
                {item?.is_module_coordinator === true ? (
                  <div>
                    <PrimaryButton
                      title="Mark complete"
                      type="submit"
                      onClick={() => {
                        handleOnCompleteClick(item?.course_details?.course_id.toString() ?? '');
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          );
        }
      })}
    </div>
  );
}

function RoutineSessionDetailPage({}: Props) {
  const { routineId = '' } = useParams<{ routineId: string }>();

  const [sessionId, setSessionId] = useState('');

  const [sessionModalOpen, toggleSessionModal] = useToggle(false);
  const [dailySessionModalOpen, toggleDailySessionModal] = useToggle(false);

  const { data: courseList = [] } = useListCoursesByTrainingQuery(routineId);

  const courseCategory = courseList?.data
    ?.map((course: any) => course.courses_list)
    ?.map((item: any) => {
      return {
        id: item.id?.toString(),
        name: item.title
      };
    });
  const { data: routineData, isLoading: isRoutingLoading } = useListRoutineByIdQuery(routineId, {
    skip: !routineId
  });

  const finalizedStatus = routineData?.save_as_draft;

  const routineInfo = (routineData ?? {}) as IRoutineInfo;
  const [saveRoutineId, setSaveRoutineId] = useState<string | null>();
  const [routineFinalized, setRoutineFinalized] = useState<boolean>(false);
  const [editSessionData, setEditSessionData] = useState<IRoutineSessionDetail | null>();

  const {
    data: savedDraft,
    isLoading: isSaving,
    isSuccess
  } = useSaveRoutineQuery(saveRoutineId, { skip: !saveRoutineId });

  useEffect(() => {
    toast.success(savedDraft?.message);
  }, [savedDraft]);

  const {
    data: sessionList = [],
    isLoading: isSessionsLoading
  }: { data: IRoutineSessionDetail[]; isLoading: boolean } = useListRoutineSessionQuery(routineId);

  if (isRoutingLoading) {
    return <Spinner />;
  }

  return (
    <Container title="Routine Info">
      <h4>Training Routine Details</h4>

      <div>
        <div className="row">
          <div className="col-lg-6 col-sm-12 border-end border-light">
            <div className="card-body p-4">
              {/* Section 1: Primary Header Info */}
              <div className="mb-4">
                <h5 className="text-primary fw-bold mb-1">
                  {routineInfo.training_details?.training_name || 'Untitled Training'}
                </h5>
                <span className="badge bg-light text-secondary border">
                  Code: {routineInfo.training_details?.training_code}
                </span>
              </div>

              <div className="row g-4">
                {/* Group: General Logistics */}
                <div className="col-12">
                  <div className="row g-2">
                    <DetailItem
                      col="col-6 col-md-3"
                      label="Hall"
                      value={routineInfo.hall_details?.hall_name}
                    />
                    <DetailItem col="col-6 col-md-3" label="Batch" value={routineInfo.batch_name} />
                    <DetailItem
                      col="col-6 col-md-3"
                      label="Start Date"
                      value={routineInfo.start_date ? `${routineInfo.start_date} A.D` : 'N/A'}
                    />
                    <DetailItem
                      label="End Date"
                      col="col-6 col-md-3"
                      value={routineInfo.end_date ? `${routineInfo.end_date} A.D` : 'N/A'}
                    />
                  </div>
                </div>

                <div className="col-12">
                  <DetailItem
                    label="Coordinator"
                    value={
                      routineInfo.tr_routine_coordinator
                        ?.filter((c) => !c.is_module_coordinator)
                        .map(
                          (c) =>
                            `${c.coordinator_details.first_name} ${c.coordinator_details.last_name}`
                        )
                        .join(', ') || 'N/A'
                    }
                  />
                </div>

                {/* Group: Attendance & Buffers (Separated Visually) */}
                <div className="col-12 mt-4">
                  <div className="p-3 bg-light rounded-3 border border-dashed">
                    <h6 className="small fw-bold text-uppercase text-muted mb-3">
                      Attendance Settings
                    </h6>
                    <div className="row g-3">
                      <DetailItem
                        label="Check-In"
                        value={
                          routineInfo.attendance_checkin_time
                            ? tConvert(routineInfo.attendance_checkin_time)
                            : '-'
                        }
                        col="col-6 col-md-3"
                      />
                      <DetailItem
                        label="Check-Out"
                        value={
                          routineInfo.attendance_checkout_time
                            ? tConvert(routineInfo.attendance_checkout_time)
                            : '-'
                        }
                        col="col-6 col-md-3"
                      />

                      {/* Buffers Styled as secondary info */}
                      <div className="col-12 border-top pt-3 mt-2">
                        <div className="row g-2">
                          <BufferBadge label="Early In" value={routineInfo.buffer_time_before} />
                          <BufferBadge label="Late In" value={routineInfo.buffer_time_after} />
                          <BufferBadge
                            label="Early Out"
                            value={routineInfo?.checkout_buffer_time_before}
                          />
                          <BufferBadge
                            label="Late Out"
                            value={routineInfo?.checkout_buffer_time_after}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 col-sm-12">
            <div className="card-body">
              <h5 className="mb-4">TraineeDetails</h5>

              <InviteUsersModal selectedRoutine={routineId} />
            </div>
          </div>
        </div>

        {/* <hr />
        <div className="row">
          <EndandStartImage routineInfo={routineInfo} routineId={routineId} />
        </div> */}

        <hr />
        <div className="row">
          <div className="card-body">
            <CoursesElement routineInfo={routineInfo} />
          </div>
        </div>

        <hr />

        <div className="card border-0 shadow-sm rounded-3">
          {/* Header Section */}
          <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0 fw-bold text-dark">Training Sessions</h5>
              <p className="text-muted small mb-0">Manage and schedule daily routine topics</p>
            </div>
            <PrimaryButton
              type="button"
              disabled={finalizedStatus || routineFinalized}
              className={
                finalizedStatus || routineFinalized ? 'btn-outline-secondary' : 'btn-primary'
              }
              title={finalizedStatus || routineFinalized ? '✓ Routine Finalized' : 'Save Routine'}
              onClick={() => {
                if (!finalizedStatus && !routineFinalized) {
                  setSaveRoutineId(routineId);
                  setRoutineFinalized(true);
                } else {
                  toast.info('Routine Already Finalized');
                }
              }}
            />
          </div>

          <div className="card-body bg-light-subtle p-0">
            {isSessionsLoading ? (
              <div className="p-5 text-center">
                <Spinner />
              </div>
            ) : (
              <div
                className="d-flex overflow-auto p-4 gap-4"
                style={{ scrollbarWidth: 'thin', minHeight: '400px' }}>
                {sessionList &&
                  sessionList.map((session) => (
                    <div
                      key={session.id}
                      style={{ minWidth: '320px', maxWidth: '320px' }}
                      className="flex-shrink-0">
                      <div className="card h-100 border-0 shadow-sm overflow-hidden">
                        {/* Session Header */}
                        <div className="card-header bg-white border-bottom-0 pt-3 pb-0 d-flex justify-content-between align-items-start">
                          <div>
                            <h6
                              className="fw-bold mb-1 text-truncate"
                              style={{ maxWidth: '220px' }}>
                              {session.session_name}
                            </h6>
                            <span className="badge bg-soft-primary text-primary border border-primary-subtle rounded-pill">
                              <i className="ri-time-line me-1"></i>
                              {formatTime(session.start_time)} - {formatTime(session.end_time)}
                            </span>
                          </div>

                          <div className="dropdown">
                            <button
                              className="btn btn-link text-muted p-0"
                              data-bs-toggle="dropdown"
                              style={{ fontSize: '1.2rem' }}>
                              <i className="ri-more-2-fill"></i>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                              <li>
                                <button
                                  className="dropdown-item d-flex align-items-center"
                                  onClick={() => {
                                    setEditSessionData(session);
                                    toggleSessionModal();
                                  }}>
                                  <img src={editIcon} className="me-2" width="16" /> Edit Session
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>

                        {/* Session Content */}
                        <div className="card-body">
                          <div
                            className="rounded-3 bg-light p-2 mb-3"
                            style={{ minHeight: '150px' }}>
                            <RoutineSessionDailyDetail
                              sessionId={session?.id?.toString() ?? ''}
                              routineId={routineId}
                            />
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm w-100 border-dashed"
                            style={{ borderStyle: 'dashed' }}
                            onClick={() => {
                              setSessionId(session?.id?.toString() || '');
                              toggleDailySessionModal();
                            }}>
                            <i className="ri-add-line me-1"></i> Add Topic
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                {/* Add Session "Ghost" Card */}
                <div
                  style={{ minWidth: '300px' }}
                  className="flex-shrink-0 d-flex align-items-center justify-content-center">
                  <button
                    onClick={() => {
                      setEditSessionData(null);
                      toggleSessionModal();
                    }}
                    className="btn btn-outline-secondary border-dashed h-100 w-100 d-flex flex-column align-items-center justify-content-center gap-2 opacity-75"
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      borderRadius: '12px'
                    }}>
                    <div className="bg-white rounded-circle shadow-sm p-3">
                      <i className="ri-add-fill fs-3 text-primary"></i>
                    </div>
                    <span className="fw-bold">Add New Session</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <SessionModal
        routineId={routineId}
        open={sessionModalOpen}
        toggle={toggleSessionModal}
        editSessionData={editSessionData}
      />

      {dailySessionModalOpen && (
        <DailySessionModal
          trainingId={routineInfo.training_details?.id.toString()}
          routineId={routineId}
          sessionId={sessionId}
          open={dailySessionModalOpen}
          toggle={toggleDailySessionModal}
        />
      )}
    </Container>
  );
}

// Standard Label/Value pair
const DetailItem = ({ label, value, col = 'col-12' }: any) => (
  <div className={col}>
    <label
      className="d-block small text-uppercase fw-semibold text-muted mb-1"
      style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
      {label}
    </label>
    <p className="mb-0 fw-medium text-dark">{value || '-'}</p>
  </div>
);

// Compact Buffer display
const BufferBadge = ({ label, value }: any) => (
  <div className="col-6 col-md-3">
    <div className="bg-white px-2 py-1 rounded border-sm">
      <label className="d-block text-muted" style={{ fontSize: '0.65rem' }}>
        {label}
      </label>
      <span className="fw-bold small">
        {value?.slice(0, 5) || '00:00'} <small className="text-muted fw-normal">min</small>
      </span>
    </div>
  </div>
);

export default RoutineSessionDetailPage;
